import messages from "../messages/messages.js";
import {useEffect, useState, useContext} from "preact/hooks";
import {useLocation} from "../hooks/useLocation.js";
import {handleRandomFavorites, getRandomSongUrl} from "../includes/requests.js";
import {signOut} from "firebase/auth";
import {auth} from "../includes/firebase.js";
import {useAuth} from '../components/AuthProvider.jsx';
import {sendEmailVerification} from "firebase/auth";
import {NotificationContext} from "../components/NotificationContext.jsx";
import {Bars3Icon, ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon} from "@heroicons/react/24/solid";
import {LoadingContext} from '../components/LoadingContext.jsx';
export function Header({isMain = true}) {

    const location = useLocation();

    const {getToken, user, authLoading} = useAuth();
    const {setLoading} = useContext(LoadingContext);
    const {addNotification} = useContext(NotificationContext);

    const userEmail = user?.email || "";
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [showVerifyBar, setShowVerifyBar] = useState(false);
    const [randomSongUrl, setRandomSongUrl] = useState(null);

    const refreshRandomSongUrl = () => {
        getRandomSongUrl().then(setRandomSongUrl);
    };

    // Prefetch so the "Random Song" link is ready before the user clicks it.
    useEffect(() => {
        refreshRandomSongUrl();
    }, []);

    const handleLogout = async () => {
        await signOut(auth);
        location.route("/");
    }

    // Close search when clicking outside
    useEffect(() => {
        const handleClick = (e) => {
            if (e.target.closest(".search, .search-toggle, .dropdown-account, .toggle-dropdown-account") === null) {
                setSearchOpen(false);
                setDropdownOpen("");
            }
        };
        const handleKeyDown = (e) => {
            if (e.target.closest(".search") !== null && e.key === "Enter") {
                location.route("/search?q=" + encodeURIComponent(e.target.closest(".search").querySelector(".input").value));
                setSearchOpen(false);
            }
        };
        document.addEventListener("click", handleClick);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("click", handleClick);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [location]);

    useEffect(() => {
        if (user && !user.emailVerified) setShowVerifyBar(true);
        else setShowVerifyBar(false);
    }, [user, location]);

    const handleResendVerification = async () => {
        if (!user || resendCooldown > 0) return;
        try {
            await sendEmailVerification(user, {url: "https://" + messages["DOMAIN"]});
            addNotification('Verification email sent. Please check your inbox.', 'success');
            setResendCooldown(60);
            const interval = setInterval(() => {
                setResendCooldown(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (e) {
            addNotification('Failed to send verification email.', 'error');
        }
    };

    // Menu items for reuse
    const mainMenuItems = (
        <>
            <div className="relative">
                <button
                    className="inline-flex items-center text-center toggle-dropdown-account cursor-pointer text-gray-400 hover:text-white"
                    aria-expanded={dropdownOpen === "explore"}
                    aria-haspopup="true"
                    onClick={() => setDropdownOpen("explore")}>Explore
                    <ChevronDownIcon className="w-4 h-4 ml-2" aria-hidden="true"/>
                </button>
                <div
                    className={(dropdownOpen === "explore" ? "block lg:absolute" : "hidden") + " dropdown-account bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-44 "}
                    style={{top: 32}}>
                    <ul className="py-2 text-sm text-gray-700 list-none">
                        <li>
                            <a href="/playlists"
                               onClick={() => setMenuOpen(false)}
                               className="block px-4 py-2 hover:bg-gray-100 text-gray-900 cursor-pointer">
                                Playlists
                            </a>
                        </li>
                        {randomSongUrl && (
                            <li>
                                <a href={randomSongUrl} onClick={() => {
                                    refreshRandomSongUrl();
                                    setMenuOpen(false);
                                }}
                                   className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-900 cursor-pointer">
                                    Random Song
                                </a>
                            </li>
                        )}
                        <li>
                            <a href={`https://fm.${messages.DOMAIN}`} target={"_blank"} rel="noopener noreferrer"
                               onClick={() => setMenuOpen(false)}
                               className="block px-4 py-2 hover:bg-gray-100 text-gray-900 cursor-pointer">
                                Radio
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="relative">
                <button
                    className="inline-flex items-center text-center toggle-dropdown-account cursor-pointer text-gray-400 hover:text-white"
                    aria-expanded={dropdownOpen === "community"}
                    aria-haspopup="true"
                    onClick={() => setDropdownOpen("community")}>Community
                    <ChevronDownIcon className="w-4 h-4 ml-2" aria-hidden="true"/>
                </button>
                <div
                    className={(dropdownOpen === "community" ? "block lg:absolute" : "hidden") + " dropdown-account bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-44 "}
                    style={{top: 32}}>
                    <ul className="py-2 text-sm text-gray-700 list-none">
                        <li>
                            <a href="/network"
                               onClick={() => setMenuOpen(false)}
                               className="block px-4 py-2 hover:bg-gray-100 text-gray-900 cursor-pointer">
                                Network
                            </a>
                        </li>
                        <li>
                            <a href="/about-us"
                               onClick={() => setMenuOpen(false)}
                               className="block px-4 py-2 hover:bg-gray-100 text-gray-900 cursor-pointer">
                                About Us
                            </a>
                        </li>
                        {messages["DISCORD_URL"] && (
                            <li>
                                <a href={messages["DISCORD_URL"]} target="_blank" rel="noopener noreferrer"
                                   onClick={() => setMenuOpen(false)}
                                   className="block px-4 py-2 hover:bg-gray-100 text-gray-900 cursor-pointer">
                                    Join Our Discord
                                </a>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
            {!user ? (
                <a href="/login" onClick={() => setMenuOpen(false)}
                   className="text-gray-400 hover:text-white">
                    Account
                </a>
            ) : (
                <div className="relative">
                    <button
                        className="inline-flex items-center text-center toggle-dropdown-account cursor-pointer text-gray-400 hover:text-white"
                        aria-expanded={dropdownOpen === "account"}
                        aria-haspopup="true"
                        onClick={() => setDropdownOpen("account")}>Account
                        <ChevronDownIcon className="w-4 h-4 ml-2" aria-hidden="true"/>
                    </button>
                    <div
                        className={(dropdownOpen === "account" ? "block lg:absolute" : "hidden") + " dropdown-account bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-44 "}
                        style={{top: 32}}>
                        <div className="px-4 py-3 text-sm text-gray-900">
                            <div>Email address:</div>
                            <div className="text-black truncate">{userEmail}</div>
                        </div>
                        <ul className="py-2 text-sm text-gray-700 list-none">
                            <li>
                                <a href="/account"
                                   onClick={() => {
                                       setDropdownOpen(false);
                                       setMenuOpen(false);
                                   }}
                                   className="block px-4 py-2 hover:bg-gray-100 text-gray-900">Dashboard</a>
                            </li>
                             <li>
                                 <button type="button" onClick={() => {
                                     handleRandomFavorites(getToken, location, setLoading, addNotification);
                                     setDropdownOpen(false);
                                     setMenuOpen(false);
                                 }}
                                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-900 cursor-pointer">Favorites</button>
                             </li>
                            <li>
                                <a href="/account/playlists"
                                   onClick={() => {
                                       setDropdownOpen(false);
                                       setMenuOpen(false);
                                   }}
                                   className="block px-4 py-2 hover:bg-gray-100 text-gray-900 cursor-pointer">My
                                    playlists</a>
                            </li>
                        </ul>
                        <div className="py-2">
                            <button type="button" onClick={() => handleLogout()}
                               className="block w-full text-left px-4 cursor-pointer py-2 text-sm text-gray-900 hover:bg-gray-100">Sign
                                out</button>
                        </div>
                    </div>
                </div>
            )}
            <MagnifyingGlassIcon
                className="w-5 h-5 hidden md:inline-block text-gray-400 hover:text-white cursor-pointer search-toggle"
                aria-hidden="true"
                onClick={() => setSearchOpen(!searchOpen)}/>
            <div className={(searchOpen ? "hidden md:block" : "hidden") + " search"}>
                <input type="text" placeholder="Search..." name="search"
                       aria-label="Search songs"
                       className="inline-block text-white bg-transparent border-b border-white focus:outline-hidden input"/>
            </div>
        </>
    );

    const secondaryMenuItems = (
        <>
            <a href="#videos"
               className="text-gray-400 hover:text-white">
                New songs
            </a>
            <a href="#support"
               className="text-gray-400 hover:text-white">
                Support me
            </a>
            <a href="#more"
               className="text-gray-400 hover:text-white">
                More
            </a>
            <a href="#about"
               className="text-gray-400 hover:text-white">
                About me
            </a>
        </>
    );

    const actionButtons = !isMain ? null : (
        <>
            <a href={messages["DISCORD_URL"]} target={"_blank"} rel="noopener noreferrer" className="secondary-btn text-center flex-grow">
                Discord
            </a>
            <a href="/subscribe" onClick={() => setMenuOpen(false)} className="primary-btn text-center flex-grow">
                Subscribe
            </a>
        </>
    );

    return (
        <>
            {showVerifyBar && (
                <div className="fixed bottom-0 left-0 w-full z-50 flex items-center justify-center bg-zinc-700 text-white text-xs py-2 px-4 shadow border-b border-zinc-600" style={{minHeight: '32px'}}>
                    <span>Your email is not verified. Please check your inbox and verify your email to unlock all features.</span>
                    <button
                        className="cursor-pointer ml-4 px-3 py-1 rounded bg-zinc-600 text-white disabled:opacity-60 text-xs border border-zinc-500"
                        onClick={handleResendVerification}
                        disabled={resendCooldown > 0}
                    >
                        {resendCooldown > 0 ? `Resend (${resendCooldown})` : 'Resend verification email'}
                    </button>
                </div>
            )}
            <nav
                className="fixed w-full top-0 z-40 bg-background/60 backdrop-blur supports-[backdrop-filter]:bg-background/50 px-4">
                <div className="flex items-center justify-between h-14 ">
                    <a href="/" onClick={() => setMenuOpen(false)} className="md:w-[20%]">
                        <img alt="website logo" height={20} width={160} src={"/img/" + messages.LOGO}/>
                    </a>
                    <div className="flex md:hidden items-center gap-3">
                        <button aria-label="Toggle search" aria-expanded={searchOpen} className="search-toggle" onClick={() => setSearchOpen(!searchOpen)}>
                            <MagnifyingGlassIcon
                                className="w-5 h-5 text-gray-400 hover:text-white"/>
                        </button>
                        <button aria-label="Open menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>
                            <Bars3Icon className="h-6 w-6 text-white"/>
                        </button>
                    </div>
                    <div
                        className={`hidden md:flex items-center gap-6 text-sm flex-grow justify-center ${!isMain ? "w-full justify-end" : ""}`}>
                        {isMain ? mainMenuItems : secondaryMenuItems}
                    </div>
                    {isMain && (
                        <div className="hidden md:flex items-center gap-4 md:w-[20%]">
                            {actionButtons}
                        </div>
                    )}
                </div>
                {/* Mobile search dropdown */}
                <div className={`md:hidden search overflow-hidden transition-all duration-200 ${searchOpen ? "max-h-14 py-2" : "max-h-0"}`}>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            placeholder="Search..."
                            name="search"
                            aria-label="Search songs"
                            autoFocus={searchOpen}
                            className="flex-1 text-white bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 focus:outline-none focus:border-white input"
                        />
                        <button
                            onClick={(e) => {
                                const input = e.target.closest(".search").querySelector(".input");
                                if (input.value) {
                                    location.route("/search?q=" + encodeURIComponent(input.value));
                                    setSearchOpen(false);
                                }
                            }}
                            aria-label="Submit search"
                            className="px-3 py-2 bg-zinc-700 border border-zinc-600 text-gray-300 hover:text-white hover:bg-zinc-600 rounded-lg transition-colors">
                            <MagnifyingGlassIcon className="w-5 h-5" aria-hidden="true"/>
                        </button>
                    </div>
                </div>
            </nav>

            <div
                className={`fixed inset-0 z-50 transition-opacity duration-300 ease-in-out lg:hidden ${
                    menuOpen ? "pointer-events-auto" : "opacity-0 pointer-events-none"} bg-black/60`}>
            <div
                className={`fixed right-0 top-0 h-full w-72 bg-zinc-800 shadow-2xl p-4 flex flex-col gap-6 z-50
                transition-transform duration-300 ease-in-out ${menuOpen ? "translate-x-0" : "translate-x-full"}`}
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu">
                <button aria-label="Close menu" className="self-end" onClick={() => setMenuOpen(false)}>
                    <XMarkIcon className="w-6 h-6 text-white"/>
                </button>
                    {isMain ? mainMenuItems : secondaryMenuItems}
                    <div className="flex items-center gap-4 mt-auto">
                        {actionButtons}
                    </div>
                </div>
            </div>
        </>
    );

}
